import React, { useEffect, useMemo, useState } from "react";
import { Modal, Input, Select, Button, message } from "antd";
import { translations } from "../../utils/Translation/staticTranslation";
import { isValidCreateUserEmail } from "../../utils/createRolesHelpers";
import "../../styles/Pages/create-roles.css";

const CreateUserModal = ({
  open,
  onClose,
  onSave,
  roleOptions,
  editingUser,
  initialRoleId,
}) => {
  const lang = localStorage.getItem("language") || "ar";
  const t = translations[lang]?.createRolesPage || {};

  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState(undefined);
  const [submitting, setSubmitting] = useState(false);

  const isEdit = Boolean(editingUser);

  useEffect(() => {
    if (!open) return;
    if (editingUser) {
      setEmail(String(editingUser.email || "").trim());
      setRoleId(initialRoleId);
      setSubmitting(false);
    } else {
      setEmail("");
      setRoleId(undefined);
      setSubmitting(false);
    }
  }, [open, editingUser, initialRoleId]);

  const modalTitle = useMemo(
    () => (
      <div className="create-user-modal-title-wrap">
        <div className="create-user-modal-title-main">
          {isEdit ? t.modal_title_edit_user : t.modal_title_create_user}
        </div>
        <div className="create-user-modal-title-sub">
          {isEdit ? t.modal_subtitle_edit_user : t.modal_subtitle_create_user}
        </div>
      </div>
    ),
    [
      isEdit,
      t.modal_title_create_user,
      t.modal_title_edit_user,
      t.modal_subtitle_create_user,
      t.modal_subtitle_edit_user,
    ],
  );

  const handleSubmit = async () => {
    if (!roleOptions?.length) {
      message.warning(t.validation_no_roles_yet);
      return;
    }
    const trimmed = email.trim();
    if (!trimmed) {
      message.error(t.validation_email_required);
      return;
    }
    if (!isValidCreateUserEmail(trimmed)) {
      message.error(t.validation_email_invalid);
      return;
    }
    if (roleId == null || roleId === "") {
      message.error(t.validation_role_required);
      return;
    }
    const role = roleOptions.find((o) => o.value === roleId);
    setSubmitting(true);
    try {
      await Promise.resolve(
        onSave({
          userId:
            isEdit && editingUser?.apiUserId != null
              ? String(editingUser.apiUserId)
              : undefined,
          email: trimmed,
          roleId,
          roleName: role?.label || "",
        }),
      );
      onClose();
    } catch {
      // Parent showed error — keep modal open
    } finally {
      setSubmitting(false);
    }
  };

  const isRTL = lang === "ar";

  return (
    <Modal
      title={modalTitle}
      open={open}
      onCancel={onClose}
      width={520}
      centered
      destroyOnClose
      footer={null}
      className="create-user-modal"
      maskClosable={false}
    >
      <div className="create-user-modal-inner" dir={isRTL ? "rtl" : "ltr"}>
        <div className="create-user-modal-field">
          <label
            className="create-user-modal-label"
            htmlFor="create-user-email-input"
          >
            {t.user_email_label}
          </label>
          <Input
            id="create-user-email-input"
            className="create-user-modal-input"
            size="large"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t.user_email_placeholder}
            autoComplete="off"
            disabled={isEdit}
          />
        </div>

        <div className="create-user-modal-field">
          <label
            className="create-user-modal-label"
            htmlFor="create-user-role-select"
          >
            {t.user_role_label}
          </label>
          <Select
            id="create-user-role-select"
            className="create-user-modal-select"
            size="large"
            popupClassName="create-user-modal-select-dropdown"
            style={{ width: "100%" }}
            placeholder={t.user_role_placeholder}
            value={roleId}
            onChange={setRoleId}
            allowClear
            options={roleOptions}
            disabled={!roleOptions?.length}
          />
        </div>

        <div className="create-user-modal-footer">
          <Button
            type="default"
            className="create-user-modal-cancel"
            onClick={onClose}
            disabled={submitting}
          >
            {t.cancel}
          </Button>
          <Button
            className="create-roles-action-btn create-user-modal-submit"
            onClick={handleSubmit}
            loading={submitting}
            disabled={submitting}
          >
            {isEdit ? t.submit_update_user : t.submit_create_user}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CreateUserModal;
